// Product Video Overlay
// This class handles the video overlay functionality for the product gallery

class ProductVideoOverlay {
  constructor() {
    // Create the overlay container if it doesn't exist
    this.createOverlayContainer();
    
    // Initialize variables
    this.isOverlayOpen = false;
    this.currentVideo = null;
    
    // Add event listeners to video thumbnails in the product gallery
    this.addVideoEventListeners();
  }
  
  createOverlayContainer() {
    // Check if the overlay container already exists
    if (document.getElementById('product-video-overlay')) {
      return;
    }
    
    // Create the overlay container
    const overlayContainer = document.createElement('div');
    overlayContainer.id = 'product-video-overlay';
    overlayContainer.className = 'product-video-overlay';
    overlayContainer.innerHTML = `
      <div class="video-overlay-container">
        <button type="button" class="video-overlay-close" aria-label="Close video">
          <span class="icon-close" aria-hidden="true">×</span>
        </button>
        <div class="video-container"></div>
      </div>
    `;
    
    // Add overlay styles
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .product-video-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.9);
        z-index: 9999;
        display: none;
        justify-content: center;
        align-items: center;
      }
      
      .product-video-overlay.active {
        display: flex;
      }
      
      .video-overlay-container {
        position: ;
        width: 90%;
        max-width: 1000px;
        max-height: 90vh;
        overflow: hidden;
        border-radius: 6px;
        background: #000;
      }
      
      .video-overlay-close {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 32px;
        height: 32px;
        background: rgba(255, 255, 255, 0.3);
        border: none;
        border-radius: 50%;
        color: white;
        font-size: 20px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
        transition: background 0.2s;
      }
      
      .video-overlay-close:hover {
        background: rgba(255, 255, 255, 0.5);
      }
      
      .video-container {
        position: relative;
        width: 100%;
        padding-top: 56.25%; /* 16:9 aspect ratio */
      }
      
      .video-container iframe,
      .video-container video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
      }
    `;
    
    // Append the overlay container and styles to the body
    document.head.appendChild(styleElement);
    document.body.appendChild(overlayContainer);
    
    // Add event listener to close button
    const closeButton = overlayContainer.querySelector('.video-overlay-close');
    closeButton.addEventListener('click', () => this.closeOverlay());
    
    // Add event listener to close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOverlayOpen) {
        this.closeOverlay();
      }
    });
    
    // Add event listener to close when clicking outside the video container
    overlayContainer.addEventListener('click', (e) => {
      if (e.target === overlayContainer) {
        this.closeOverlay();
      }
    });
  }
  
  addVideoEventListeners() {
    // Make sure we override the default deferred media behavior first
    this.overrideDeferredMediaBehavior();
    
    // Find all deferred media elements (videos) in the product gallery
    const mediaItems = document.querySelectorAll('.product__media-item');
    
    mediaItems.forEach(item => {
      // Check if this is a video item
      const deferredMedia = item.querySelector('.deferred-media');
      if (!deferredMedia) return;
      
      // Find clickable elements within this media item
      const clickTargets = [
        item.querySelector('.product__media'),
        item.querySelector('.deferred-media__poster'),
        item.querySelector('.deferred-media__poster-button')
      ].filter(Boolean);
      
      // Add click event listeners to all potential targets
      clickTargets.forEach(target => {
        target.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Get the video URL from the deferred media template
          const deferredMediaTemplate = deferredMedia.querySelector('template');
          if (!deferredMediaTemplate) return;
          
          // Prevent the default deferred media loading behavior
          const deferredMediaLoadButton = deferredMedia.querySelector('.deferred-media__poster-button');
          if (deferredMediaLoadButton && typeof deferredMediaLoadButton.dispatchEvent !== 'function') {
            // If the button is an object but not a proper DOM element, find it differently
            const alternateButton = deferredMedia.querySelector('button.deferred-media__poster-button');
            if (alternateButton) {
              // Store original click behavior
              if (!alternateButton.hasAttribute('data-original-click')) {
                const originalClickHandler = alternateButton.onclick;
                alternateButton.setAttribute('data-original-click', 'stored');
                alternateButton.onclick = null;
              }
            }
          }
          
          // Open the overlay with the video
          this.openOverlay(deferredMediaTemplate);
        });
      });
    });
    
    // Also add listeners to any thumbnail items that represent videos
    const thumbnailItems = document.querySelectorAll('.thumbnail-list__item');
    thumbnailItems.forEach(thumbnail => {
      // Check if this thumbnail corresponds to a video
      const targetId = thumbnail.dataset.target;
      if (!targetId) return;
      
      const targetMedia = document.querySelector(`.product__media-item[data-media-id="${targetId}"]`);
      if (!targetMedia) return;
      
      const deferredMedia = targetMedia.querySelector('.deferred-media');
      if (!deferredMedia) return;
      
      // This thumbnail corresponds to a video, add a click event listener
      thumbnail.addEventListener('click', (e) => {
        // We don't want to prevent default here as it would break the thumbnail navigation
        
        // We need to use setTimeout to let the thumbnail switch happen first
        setTimeout(() => {
          // Now prevent the deferred media from loading in the gallery
          const deferredMediaButton = deferredMedia.querySelector('.deferred-media__poster-button');
          if (deferredMediaButton) {
            // Prevent default deferred media behavior
            if (typeof deferredMediaButton.click === 'function') {
              deferredMediaButton.onclick = (e) => {
                if (e) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                return false;
              };
            }
          }
          
          // Get the video URL from the deferred media template
          const deferredMediaTemplate = deferredMedia.querySelector('template');
          if (!deferredMediaTemplate) return;
          
          // Open the overlay with the video
          this.openOverlay(deferredMediaTemplate);
        }, 10); // Small delay to let thumbnail navigation complete
      });
    });
  }
  
  openOverlay(deferredMediaTemplate) {
    // Set the global flag that we're opening in overlay mode
    window.openingInOverlay = true;
    
    // Get the overlay container
    const overlayContainer = document.getElementById('product-video-overlay');
    const videoContainer = overlayContainer.querySelector('.video-container');
    
    // Clear any existing content
    videoContainer.innerHTML = '';
    
    // Clone and append the template content to the video container
    const templateContent = deferredMediaTemplate.content.cloneNode(true);
    videoContainer.appendChild(templateContent);
    
    // Store reference to the video element for later use when closing
    this.currentVideo = videoContainer.querySelector('video, iframe');
    
    // If it's a video element, set it to autoplay
    if (this.currentVideo.tagName === 'VIDEO') {
      this.currentVideo.setAttribute('autoplay', 'autoplay');
      // Ensure the video is not muted
      this.currentVideo.muted = false;
      // Set volume to a good level (1.0 is maximum)
      this.currentVideo.volume = 1.0;
      this.currentVideo.play();
    }
    
    // If it's an iframe (YouTube, Vimeo), ensure it has the autoplay parameter and is not muted
    if (this.currentVideo.tagName === 'IFRAME') {
      let src = this.currentVideo.getAttribute('src');
      if (src) {
        // Add autoplay parameter and ensure sound is on
        if (src.includes('youtube') || src.includes('youtu.be')) {
          // YouTube - autoplay=1 AND mute=0 (important to include both)
          if (src.includes('?')) {
            src = src + '&autoplay=1&mute=0';
          } else {
            src = src + '?autoplay=1&mute=0';
          }
        } else if (src.includes('vimeo')) {
          // Vimeo - autoplay=1 AND muted=0 (important to include both)
          if (src.includes('?')) {
            src = src + '&autoplay=1&muted=0';
          } else {
            src = src + '?autoplay=1&muted=0';
          }
        }
        
        this.currentVideo.setAttribute('src', src);
      }
    }
    
    // Show the overlay
    overlayContainer.classList.add('active');
    this.isOverlayOpen = true;
    
    // Disable scrolling on the body
    document.body.style.overflow = 'hidden';
  }
  
  closeOverlay() {
    // Reset the global flag
    window.openingInOverlay = false;
    
    // Get the overlay container
    const overlayContainer = document.getElementById('product-video-overlay');
    
    // Stop the video
    if (this.currentVideo) {
      if (this.currentVideo.tagName === 'VIDEO') {
        this.currentVideo.pause();
      } else if (this.currentVideo.tagName === 'IFRAME') {
        // For iframes, the best way to stop them is to set src to empty or remove the element
        this.currentVideo.setAttribute('src', '');
      }
      this.currentVideo = null;
    }
    
    // Hide the overlay
    overlayContainer.classList.remove('active');
    this.isOverlayOpen = false;
    
    // Clear the video container
    const videoContainer = overlayContainer.querySelector('.video-container');
    videoContainer.innerHTML = '';
    
    // Enable scrolling on the body
    document.body.style.overflow = '';
  }
  
  overrideDeferredMediaBehavior() {
    // This function prevents the default Shopify deferred media behavior for videos
    // in the product gallery while still allowing our overlay to work
    
    // First, let's try to intercept the default behavior by extending the DeferredMedia class
    if (typeof customElements !== 'undefined') {
      // Check if there's an existing DeferredMedia custom element
      const existingDeferredMedia = customElements.get('deferred-media');
      
      if (existingDeferredMedia) {
        // We need to keep track of whether we're opening in overlay mode or not
        window.openingInOverlay = false;
        
        // Create a new class that extends the existing one
        class CustomDeferredMedia extends existingDeferredMedia {
          constructor() {
            super();
            this.hasOverrideApplied = true;
          }
          
          // Override the loadContent method
          loadContent() {
            if (window.openingInOverlay) {
              // If we're opening in overlay mode, don't load in the original container
              return;
            }
            
            // Otherwise, call the original loadContent method
            super.loadContent();
          }
        }
        
        // Replace the existing DeferredMedia with our custom one
        try {
          customElements.define('deferred-media', CustomDeferredMedia, { extends: 'deferred-media' });
        } catch (error) {
          // If we can't redefine it, we'll use alternate methods
          console.log('Could not extend DeferredMedia, using alternate methods');
        }
      }
    }
    
    // Second approach: directly modify the click behavior of all deferred media buttons
    const deferredMediaButtons = document.querySelectorAll('.deferred-media__poster-button');
    deferredMediaButtons.forEach(button => {
      // Store the original click handler
      const originalClick = button.onclick;
      
      // Replace with our own handler
      button.onclick = function(e) {
        if (window.openingInOverlay) {
          // If we're in overlay mode, prevent default behavior
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
        
        // If we're not in overlay mode, use the original handler
        if (originalClick) {
          return originalClick.call(this, e);
        }
      };
    });
  }
}

// Initialize the video overlay functionality when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const productVideoOverlay = new ProductVideoOverlay();
  
  // Make it globally accessible for debugging
  window.productVideoOverlay = productVideoOverlay;
});

// Add an extra event listener for dynamically loaded content
document.addEventListener('shopify:section:load', (event) => {
  // If this is a product section, reinitialize the video overlay
  if (event.target.classList.contains('section-main-product')) {
    // Wait a moment for the content to fully load
    setTimeout(() => {
      new ProductVideoOverlay();
    }, 100);
  }
});